import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import { StyledPage, PageInner, StyledIframe, Loader, htmlEditorStyles } from './styles';
import { StoreState } from '../../../reducers';
import Content from '../../../containers/Editor/Page/Content';
import RichTextEditor from '../../../containers/Editor/RichTextEditor';
import MediaEditor from '../../../containers/Editor/MediaEditor';
import LinkEditor from '../../../containers/Editor/LinkEditor';
import ButtonEditor from '../../../containers/Editor/ButtonEditor';
import IframeEditor from '../../../containers/Editor/IframeEditor';
import EnrichmentEditor from '../../../containers/Editor/EnrichmentEditor';
import SliderEditor from '../../../containers/Editor/SliderEditor';
import ThemedItemEditor from '../../../containers/Editor/ThemedItemEditor';
import Portal from '../../Portal';
import constants from '../../../containers/Editor/utils/indesignEditor/constants';
import { resetActiveItem } from '../../../containers/Editor/actions';
import { EditorType, editedElementClassName, ShowAloneSide, IGNORE_PAGE_CLICK_CLASSNAME, SPREAD_BACKGROUND_CLICK_CLASSNAME } from '../../../containers/Editor/constants';
import { editableId, editorStylesId, editorFontsId, editorGoogleFontsId } from '../../../containers/Editor/utils/htmlEditor/constants';
import { createNotification } from '../../../containers/NotificationBar/actions';
import Utils from '../../../utils';
import { IndesignMediaElementConfig } from '../../../containers/Editor/utils/indesignEditor/editableMedia/getData';
import { EditableMediaState } from '../../../containers/Editor/reducer';
const svgSpritePath = require('../../../images/icons.svg');

interface PageProps {
	editorType: EditorType;
	page: number;
	width: number;
	height: number;
	spreadZoom: number;
	pageScale: number;
	isLoaded: boolean;
	html: string;
	iframeUrl: string;
	fontsPath: string;
	googleFontsPath: string;
	isEditable: boolean;
	editableTextFieldsIds: string[];
	pageEditableMedia: IndesignMediaElementConfig[];
	editableMediaIds: string[];
	editableLinksIds: string[];
	editableButtonsIds: string[];
	editableIframesIds: string[];
	editableEnrichmentsIds: string[];
	editableSlidersIds: string[];
	themedItemsIds: string[];
	isSomeEditableItemActive: boolean;
	editorContainer: Element | null;
	isTransparent: boolean;
	showAloneSide: ShowAloneSide | undefined;
	dispatch: Dispatch<StoreState>;
	onLoad?: (page: number) => void;
	isAccessibleVersion: boolean;
	editableMedia: EditableMediaState;
}

const Page = ({
	editorType,
	page,
	width,
	height,
	spreadZoom,
	pageScale,
	isLoaded,
	html,
	iframeUrl,
	fontsPath,
	googleFontsPath,
	isEditable,
	editableTextFieldsIds,
	editableLinksIds,
	editableButtonsIds,
	editableIframesIds,
	editableEnrichmentsIds,
	editableSlidersIds,
	themedItemsIds,
	isSomeEditableItemActive,
	editorContainer,
	isTransparent,
	showAloneSide,
	dispatch,
	onLoad = () => undefined,
	isAccessibleVersion,
	pageEditableMedia,
	editableMediaIds,
	editableMedia,
}: PageProps) => {
	// is needed for initial render to prevent page jumping (when page sizes are not calculated)
	const [isDisplayed, setIsDisplayed] = React.useState(false);
	const [isIframeDocumentReady, setIsIframeDocumentReady] = React.useState(false);

	const iframe = React.useRef<HTMLIFrameElement>(null);

	React.useEffect(() => {
		setIsDisplayed(true);

		return () => {
			setIsDisplayed(false);
		};
	}, [page]);

	React.useEffect(() => {
		if (!isSomeEditableItemActive) {
			return;
		}

		const handleWindowClick = (e: MouseEvent | TouchEvent) => {
			const element = e.target as HTMLElement;
			const shouldIgnoreClick = !!element.closest(`.${IGNORE_PAGE_CLICK_CLASSNAME}`);

			if (!shouldIgnoreClick) {
				dispatch(resetActiveItem());
			}
		};

		if (editorContainer) {
			editorContainer.addEventListener('mousedown', handleWindowClick);
		}

		if (iframe.current) {
			iframe.current.contentDocument!.addEventListener('mousedown', handleWindowClick);
		}

		if (Utils.isEmbedMode()) {
			window.parent.addEventListener('mousedown', handleWindowClick);
		}

		return () => {
			if (editorContainer) {
				editorContainer.removeEventListener('mousedown', handleWindowClick);
			}

			if (iframe.current) {
				iframe.current.contentDocument!.removeEventListener('mousedown', handleWindowClick);
			}

			if (Utils.isEmbedMode()) {
				window.parent.removeEventListener('mousedown', handleWindowClick);
			}
		};
	}, [isSomeEditableItemActive]);

	React.useEffect(() => {
		if (editorType === EditorType.HTML) {
			setIsIframeDocumentReady(false);
		}
	}, []);

	React.useEffect(() => {
		if (editorType === EditorType.INDESIGN) {
			return;
		}

		// load fonts to main document to render them in text edit panel
		const addEditorFonts = () => {
			const addLinkElement = (id: string, href: string): void => {
				const linkElement = document.createElement('link');
				linkElement.id = id;
				linkElement.rel = 'stylesheet';
				linkElement.href = href;
				document.head.appendChild(linkElement);
			};

			if (fontsPath) {
				if (fontsPath) {
					addLinkElement(editorFontsId, iframeUrl.replace('index.html', fontsPath));
				}
			}

			if (googleFontsPath) {
				addLinkElement(editorGoogleFontsId, googleFontsPath);
			}
		};

		const removeEditorFonts = () => {
			const removeLinkElement = (id: string): void => {
				const linkElement = document.querySelector(`#${id}`);
				if (linkElement) {
					linkElement.parentNode!.removeChild(linkElement);
				}
			};

			removeLinkElement(editorFontsId);
			removeLinkElement(editorGoogleFontsId);
		};

		addEditorFonts();

		return () => removeEditorFonts();
	}, []);

	React.useLayoutEffect(() => {
		if (editorType === EditorType.INDESIGN && isLoaded && spreadZoom >= 1) {
			scaleLockedIcons(pageScale);
		}
	}, [editorType, isLoaded, pageScale, spreadZoom]);

	const scaleLockedIcons = (pageScale: number) => {
		const lockedIcons = [...document.querySelectorAll(`.${constants.lockedIconClassName}`)] as HTMLElement[];

		lockedIcons.forEach((icon) => {
			icon.style.transform = `scale(${1 / pageScale})`;
		});
	};

	const handleIframeLoad = async () => {
		const addEditorStyles = (iframeDocument: Document) => {
			const editorStyleElement = iframeDocument.createElement('style');
			editorStyleElement.id = editorStylesId;
			editorStyleElement.innerHTML = htmlEditorStyles;
			iframeDocument.head.appendChild(editorStyleElement);
		};

		const addSvgSprite = async (iframeDocument: Document) => {
			const svgSprite = await Utils.promiseXHR('GET', svgSpritePath, undefined, undefined, undefined, undefined, undefined, true);
			const div = iframeDocument.createElement('div');
			div.innerHTML = svgSprite.responseText;
			iframeDocument.body.appendChild(div);
		};

		try {
			const iframeDocument = iframe.current!.contentDocument as Document;
			addEditorStyles(iframeDocument);
			iframeDocument.body.innerHTML = html;
			await addSvgSprite(iframeDocument);
		} catch (error) {
			dispatch(createNotification('error.parse_editor_data_error'));
		} finally {
			setIsIframeDocumentReady(true);
		}
	};

	const getParentContainer = (id: string): HTMLElement | null => {
		const selector = editorType === EditorType.INDESIGN
			? `[data-page="${page}"] #${id}`
			: `[${editableId}=${id}]`;
		const pageDocument = iframe.current ? iframe.current.contentDocument : document;

		if (!pageDocument) {
			return null;
		}

		return pageDocument.querySelector(selector);
	};

	const renderRichTextEditors = () => (
		<>
			{editableTextFieldsIds.map((id) => {
				// tslint:disable-next-line: max-line-length
				// We have to render <RichTextEditor /> in corresponding DOM nodes in <Background /> to keep all parents of text frames generated by inDesign
				// (e.g., text frames inside groups or animated frames)
				const parentContainer = getParentContainer(id);

				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<RichTextEditor
							page={page}
							id={id}
							pageScale={pageScale}
							isEdited={parentContainer.classList.contains(editedElementClassName)}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderMediaEditors = () => (
		<>
			{/*Displays all available editable media*/}
			{isAccessibleVersion && pageEditableMedia.map((editableMediaItem) => {
				const { id } = editableMediaItem;
				const parentContainer = getParentContainer(id);

				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<MediaEditor
							page={page}
							editableMediaItem={editableMediaItem}
							pageScale={pageScale}
							parentContainer={parentContainer}
							iframeContainer={iframe.current}
						/>
					</Portal>
				);
			})}
			{/*Excludes `isDuplicatedItemInSpread` from the adjacent page*/}
			{!isAccessibleVersion && editableMediaIds.map((id) => {
				const editableMediaItem = editableMedia[page][id];
				const parentContainer = getParentContainer(id);

				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<MediaEditor
							page={page}
							editableMediaItem={editableMediaItem}
							pageScale={pageScale}
							parentContainer={parentContainer}
							iframeContainer={iframe.current}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderLinkEditors = () => (
		<>
			{editableLinksIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<LinkEditor
							page={page}
							id={id}
							pageScale={pageScale}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderButtonEditors = () => (
		<>
			{editableButtonsIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<ButtonEditor
							page={page}
							id={id}
							pageScale={pageScale}
							parentContainer={parentContainer}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderIframeEditors = () => (
		<>
			{editableIframesIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<IframeEditor
							page={page}
							id={id}
							pageScale={pageScale}
							parentContainer={parentContainer}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderEnrichmentEditors = () => (
		<>
			{editableEnrichmentsIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<EnrichmentEditor
							page={page}
							id={id}
							pageScale={pageScale}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderSliderEditors = () => (
		<>
			{editableSlidersIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<SliderEditor
							page={page}
							id={id}
							pageScale={pageScale}
							parentContainer={parentContainer}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderThemedItemEditors = () => (
		<>
			{themedItemsIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<ThemedItemEditor
							page={page}
							id={id}
							pageScale={pageScale}
						/>
					</Portal>
				);
			})}
		</>
	);

	if (editorType === EditorType.HTML) {
		return (
			<>
				{!isIframeDocumentReady &&
					<Loader>
						{Utils.getSvgIcon('iconset_loading')}
						<Translate value="messages.loading" />
					</Loader>
				}
				<StyledIframe
					ref={iframe}
					src={iframeUrl}
					isLoading={!isIframeDocumentReady}
					onLoad={handleIframeLoad}
				/>
				{isEditable && isIframeDocumentReady && (
					<>
						{renderRichTextEditors()}
						{renderMediaEditors()}
						{renderLinkEditors()}
					</>
				)}
			</>
		);
	}

	return (
		<StyledPage
			className={isTransparent ? SPREAD_BACKGROUND_CLICK_CLASSNAME : ''}
			width={isDisplayed ? width : 0}
			height={isDisplayed ? height : 0}
			isTransparent={isTransparent}
			showAloneSide={showAloneSide}
			isSomeEditableItemActive={isSomeEditableItemActive}
			isAccessibleVersion={isAccessibleVersion}
		>
			<PageInner scale={pageScale}>
				<Content
					page={page}
					onLoad={onLoad}
				/>
				{isEditable &&
					<>
						{renderRichTextEditors()}
						{renderMediaEditors()}
						{renderLinkEditors()}
						{renderButtonEditors()}
						{renderIframeEditors()}
						{renderEnrichmentEditors()}
						{renderSliderEditors()}
						{renderThemedItemEditors()}
					</>
				}
			</PageInner>
		</StyledPage>
	);
};

export default Page;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Page/index.tsx